<template>
    <div class="dlm-admin-theme agent-chat" :class="`agent-chat--${mode}`">
        <ChatLauncher
            v-if="mode === 'floating'"
            :open="open"
            :position="position"
            @toggle="toggle"
        >
            <div class="agent-chat__shell">
                <ConversationHistory
                    :endpoint="resolvedConversationsEndpoint"
                    :visible="showHistory"
                    :active-id="conversationId || ''"
                    :refresh-token="historyRefreshToken"
                    @select="resumeConversation"
                />
                <div class="agent-chat__panel">
                    <AgentChatHeader
                        :title="title"
                        :agents="agents"
                        :selected-agent="selectedAgent"
                        :providers="providers"
                        :models="models"
                        :selected-provider="selectedProvider"
                        :selected-model="selectedModel"
                        :show-close="true"
                        :show-new-chat="hasThread"
                        :agent-locked="Boolean(pinnedAgent)"
                        :llm-locked="Boolean(pinnedProvider)"
                        @update:selected-agent="onAgentChange"
                        @update:selected-provider="onProviderChange"
                        @update:selected-model="onModelChange"
                        @close="close"
                        @new-chat="startNewChat"
                    />
                    <Conversation
                        :messages="messages"
                        :streaming-content="streamingContent"
                        :status="status"
                        :error="error"
                        :can-apply="Boolean(applyTarget)"
                        :apply-label="applyLabel"
                        @apply="applyAssistantContent"
                    />
                    <PromptInput
                        v-model="draft"
                        :status="status"
                        @send="send"
                        @stop="cancel"
                    />
                </div>
            </div>
        </ChatLauncher>

        <div v-else class="agent-chat__shell">
            <ConversationHistory
                :endpoint="resolvedConversationsEndpoint"
                :visible="showHistory"
                :active-id="conversationId || ''"
                :refresh-token="historyRefreshToken"
                @select="resumeConversation"
            />
            <div class="agent-chat__panel">
                <AgentChatHeader
                    :title="title"
                    :agents="agents"
                    :selected-agent="selectedAgent"
                    :providers="providers"
                    :models="models"
                    :selected-provider="selectedProvider"
                    :selected-model="selectedModel"
                    :show-new-chat="hasThread"
                    :agent-locked="Boolean(pinnedAgent)"
                    :llm-locked="Boolean(pinnedProvider)"
                    @update:selected-agent="onAgentChange"
                    @update:selected-provider="onProviderChange"
                    @update:selected-model="onModelChange"
                    @new-chat="startNewChat"
                />
                <Conversation
                    :messages="messages"
                    :streaming-content="streamingContent"
                    :status="status"
                    :error="error"
                    :can-apply="Boolean(applyTarget)"
                    :apply-label="applyLabel"
                    @apply="applyAssistantContent"
                />
                <PromptInput
                    v-model="draft"
                    :status="status"
                    @send="send"
                    @stop="cancel"
                />
            </div>
        </div>
    </div>
</template>

<script>
import Conversation from './Conversation.vue';
import PromptInput from './PromptInput.vue';
import ChatLauncher from './ChatLauncher.vue';
import AgentChatHeader from './AgentChatHeader.vue';
import ConversationHistory from './ConversationHistory.vue';
import { createAgentChatClient } from '../../chat/agentChatClient';

const STREAM_TIMEOUT_MS = 120000;

export default {
    name: 'AgentChat',
    components: { Conversation, PromptInput, ChatLauncher, AgentChatHeader, ConversationHistory },
    props: {
        mode: {
            type: String,
            default: 'full',
            validator: (value) => ['full', 'side', 'floating'].includes(value),
        },
        endpoint: {
            type: String,
            default: '/junction-ap/api/agent/chat',
        },
        modelsEndpoint: {
            type: String,
            default: '',
        },
        agentsEndpoint: {
            type: String,
            default: '',
        },
        conversationsEndpoint: {
            type: String,
            default: '',
        },
        title: {
            type: String,
            default: 'AI Assistant',
        },
        position: {
            type: String,
            default: 'bottom-right',
        },
        startOpen: {
            type: Boolean,
            default: false,
        },
        defaultProvider: {
            type: String,
            default: '',
        },
        defaultModel: {
            type: String,
            default: '',
        },
        defaultAgent: {
            type: String,
            default: '',
        },
        /**
         * Construction-time pin: when set, always sent on chat POST and agent select is hidden.
         * Soft preference stays on defaultAgent (Junction free chat).
         */
        agent: {
            type: String,
            default: '',
        },
        /**
         * Construction-time LLM pin (desk assist). Soft preference stays on defaultProvider/defaultModel.
         */
        provider: {
            type: String,
            default: '',
        },
        model: {
            type: String,
            default: '',
        },
        applyTarget: {
            type: String,
            default: '',
        },
        applyLabel: {
            type: String,
            default: 'Use in reply',
        },
        seedEvent: {
            type: String,
            default: '',
        },
        autoApplyOnComplete: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            messages: [],
            draft: '',
            status: 'ready',
            streamingContent: '',
            error: null,
            finishReason: null,
            usage: null,
            conversationId: null,
            open: this.startOpen,
            agents: [],
            providers: [],
            models: [],
            selectedAgent: (typeof this.agent === 'string' && this.agent.trim())
                || (typeof this.defaultAgent === 'string' && this.defaultAgent.trim())
                || '',
            selectedProvider: (typeof this.provider === 'string' && this.provider.trim())
                || (typeof this.defaultProvider === 'string' && this.defaultProvider.trim())
                || '',
            selectedModel: (typeof this.model === 'string' && this.model.trim())
                || (typeof this.defaultModel === 'string' && this.defaultModel.trim())
                || '',
            historyRefreshToken: 0,
            chatClient: null,
        };
    },
    created() {
        this.chatClient = createAgentChatClient({
            endpoint: this.endpoint,
            csrfToken: () => this.csrfToken(),
            timeoutMs: STREAM_TIMEOUT_MS,
            getPins: () => ({
                agent: this.resolvedAgent,
                provider: this.resolvedProvider,
                model: this.resolvedModel,
                conversationId: this.conversationId,
            }),
            onDelta: (delta) => {
                if (this.status !== 'streaming') {
                    this.status = 'streaming';
                }
                this.streamingContent += delta;
            },
            onStatus: (status) => {
                if (status === 'submitted' || status === 'streaming' || status === 'ready') {
                    this.status = status;
                }
            },
            onComplete: ({ content, conversationId }) => {
                if (conversationId) {
                    this.conversationId = conversationId;
                }
                if (this.status === 'error') {
                    this.streamingContent = '';
                    return;
                }
                if (content) {
                    this.messages.push({ role: 'assistant', content });
                    if (this.autoApplyOnComplete && this.applyTarget) {
                        this.applyAssistantContent(content);
                    }
                }
                this.streamingContent = '';
                this.status = 'ready';
                if (this.showHistory) {
                    this.historyRefreshToken += 1;
                }
            },
            onError: ({ message, httpStatus, errorCode, providerError }) => {
                this.error = message;
                this.status = 'error';
                this.streamingContent = '';
                this.$emit('chat-error', { message, httpStatus, errorCode, providerError });
            },
        });
    },
    computed: {
        pinnedAgent() {
            return typeof this.agent === 'string' ? this.agent.trim() : '';
        },
        pinnedProvider() {
            return typeof this.provider === 'string' ? this.provider.trim() : '';
        },
        pinnedModel() {
            return typeof this.model === 'string' ? this.model.trim() : '';
        },
        resolvedAgent() {
            return this.pinnedAgent
                || (typeof this.selectedAgent === 'string' ? this.selectedAgent.trim() : '')
                || (typeof this.defaultAgent === 'string' ? this.defaultAgent.trim() : '');
        },
        resolvedProvider() {
            return this.pinnedProvider
                || (typeof this.selectedProvider === 'string' ? this.selectedProvider.trim() : '')
                || (typeof this.defaultProvider === 'string' ? this.defaultProvider.trim() : '');
        },
        resolvedModel() {
            return this.pinnedModel
                || (typeof this.selectedModel === 'string' ? this.selectedModel.trim() : '')
                || (typeof this.defaultModel === 'string' ? this.defaultModel.trim() : '');
        },
        resolvedModelsEndpoint() {
            if (this.modelsEndpoint) {
                return this.modelsEndpoint;
            }

            return this.endpoint.replace(/\/chat\/?$/, '/llmodels');
        },
        resolvedAgentsEndpoint() {
            if (this.agentsEndpoint) {
                return this.agentsEndpoint;
            }

            return this.endpoint.replace(/\/chat\/?$/, '/agents');
        },
        resolvedConversationsEndpoint() {
            if (this.conversationsEndpoint) {
                return this.conversationsEndpoint;
            }

            return this.endpoint.replace(/\/chat\/?$/, '/conversations');
        },
        selectedAgentDefinition() {
            const slug = this.resolvedAgent;
            return this.agents.find((agent) => agent.slug === slug) || null;
        },
        showHistory() {
            const runner = this.selectedAgentDefinition?.runner;

            return runner !== undefined && runner !== 'generic';
        },
        hasThread() {
            return Boolean(this.conversationId) || this.messages.length > 0;
        },
    },
    mounted() {
        this.loadCatalog();
        if (this.seedEvent) {
            window.addEventListener(this.seedEvent, this.onSeedEvent);
        }
    },
    beforeUnmount() {
        if (this.chatClient) {
            this.chatClient.cancel();
        }
        if (this.seedEvent) {
            window.removeEventListener(this.seedEvent, this.onSeedEvent);
        }
    },
    methods: {
        toggle() {
            this.open = !this.open;
        },
        close() {
            this.open = false;
        },
        onSeedEvent(event) {
            const detail = event?.detail || {};
            const prompt = typeof detail.prompt === 'string' ? detail.prompt.trim() : '';
            if (!prompt) {
                return;
            }

            if (this.mode === 'floating') {
                this.open = true;
            }

            this.draft = prompt;
            if (detail.autoSend) {
                this.$nextTick(() => this.send());
            }
        },
        applyAssistantContent(content) {
            const text = typeof content === 'string' ? content.trim() : '';
            if (!text || !this.applyTarget) {
                return;
            }

            const el = document.querySelector(this.applyTarget);
            if (!el) {
                return;
            }

            if ('value' in el) {
                el.value = text;
                el.dispatchEvent(new Event('input', { bubbles: true }));
                el.dispatchEvent(new Event('change', { bubbles: true }));
            } else {
                el.textContent = text;
            }

            this.$emit('applied', text);
        },
        async loadCatalog() {
            await Promise.all([
                this.loadAgents(),
                this.loadModelsCatalog(),
            ]);
        },
        async loadAgents() {
            try {
                const response = await fetch(this.resolvedAgentsEndpoint, {
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                });

                if (!response.ok) {
                    return;
                }

                const data = await response.json();
                this.agents = data.agents || [];

                if (this.pinnedAgent) {
                    this.selectedAgent = this.pinnedAgent;
                    return;
                }

                const preferred = (typeof this.defaultAgent === 'string' && this.defaultAgent.trim())
                    || data.default_agent
                    || 'generic';
                if (preferred && this.agents.some((item) => item.slug === preferred)) {
                    this.selectedAgent = preferred;
                } else if (typeof this.defaultAgent === 'string' && this.defaultAgent.trim()) {
                    this.selectedAgent = this.defaultAgent.trim();
                } else if (this.agents.length) {
                    this.selectedAgent = this.agents[0].slug;
                }
            } catch (e) {
                // Agent catalog is optional for rendering.
            }
        },
        async loadModelsCatalog() {
            try {
                const response = await fetch(this.resolvedModelsEndpoint, {
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                });

                if (!response.ok) {
                    return;
                }

                const data = await response.json();
                this.providers = data.providers || [];

                if (this.pinnedProvider) {
                    this.selectedProvider = this.pinnedProvider;
                    if (this.pinnedModel) {
                        this.selectedModel = this.pinnedModel;
                    }
                    await this.onProviderChange(this.pinnedProvider, this.pinnedModel || this.defaultModel || '');
                    return;
                }

                const provider = this.defaultProvider || data.default_provider || '';
                const model = this.defaultModel || data.default_model || '';

                if (provider) {
                    await this.onProviderChange(provider, model);
                }
            } catch (e) {
                // Catalog is optional for rendering; chat may still work with server defaults.
            }
        },
        onAgentChange(agent) {
            if (this.pinnedAgent) {
                return;
            }
            if (agent === this.selectedAgent) {
                return;
            }

            this.selectedAgent = agent;
            this.resetConversationThread();
        },
        onModelChange(model) {
            if (this.pinnedModel || this.pinnedProvider) {
                return;
            }
            this.selectedModel = model;
        },
        startNewChat() {
            if (this.chatClient) {
                this.chatClient.resetThread();
            }
            this.resetConversationThread();
        },
        resetConversationThread() {
            this.messages = [];
            this.conversationId = null;
            this.draft = '';
            this.error = null;
            this.streamingContent = '';
            this.finishReason = null;
            this.usage = null;
            this.status = 'ready';
            if (this.chatClient) {
                this.chatClient.setConversationId(null);
            }
        },
        async resumeConversation(item) {
            if (!item?.id) {
                return;
            }

            try {
                const response = await fetch(`${this.resolvedConversationsEndpoint}/${encodeURIComponent(item.id)}`, {
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                });

                if (!response.ok) {
                    throw new Error(`Request failed (${response.status})`);
                }

                const payload = await response.json();
                const agentSlug = payload.agent_slug;

                if (this.pinnedAgent) {
                    this.selectedAgent = this.pinnedAgent;
                } else if (agentSlug && this.agents.some((agent) => agent.slug === agentSlug)) {
                    this.selectedAgent = agentSlug;
                }

                this.messages = payload.messages || [];
                this.conversationId = payload.id;
                if (this.chatClient) {
                    this.chatClient.setConversationId(payload.id);
                }
                this.draft = '';
                this.error = null;
                this.streamingContent = '';
                this.finishReason = null;
                this.usage = null;
                this.status = 'ready';
            } catch (e) {
                this.error = 'Impossibile riaprire la conversazione.';
                this.status = 'error';
                this.$emit('chat-error', {
                    message: this.error,
                    httpStatus: null,
                    errorCode: 'conversation_resume',
                    providerError: null,
                });
            }
        },
        async onProviderChange(provider, preferredModel = '') {
            if (this.pinnedProvider && provider !== this.pinnedProvider) {
                return;
            }

            this.selectedProvider = this.pinnedProvider || provider;

            try {
                const url = `${this.resolvedModelsEndpoint}?provider=${encodeURIComponent(this.selectedProvider)}`;
                const response = await fetch(url, {
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                });

                if (!response.ok) {
                    if (this.pinnedModel) {
                        this.selectedModel = this.pinnedModel;
                    }
                    return;
                }

                const data = await response.json();
                this.models = data.models || [];
                if (this.pinnedModel) {
                    this.selectedModel = this.pinnedModel;
                } else {
                    this.selectedModel = preferredModel || data.default_model || (this.models[0]?.id ?? '');
                }
            } catch (e) {
                this.models = [];
                if (this.pinnedModel) {
                    this.selectedModel = this.pinnedModel;
                }
            }
        },
        send() {
            if (this.status !== 'ready' && this.status !== 'error') {
                return;
            }

            const content = this.draft.trim();
            if (!content) {
                return;
            }

            this.messages.push({ role: 'user', content });
            this.draft = '';
            this.error = null;
            this.streamingContent = '';
            this.finishReason = null;
            this.usage = null;
            this.status = 'submitted';
            this.chatClient.send('', { messages: this.messages });
        },
        cancel() {
            if (this.chatClient) {
                this.chatClient.cancel();
            }
        },
        csrfToken() {
            const meta = document.head.querySelector('meta[name="csrf-token"]');

            return meta ? meta.getAttribute('content') : '';
        },
    },
};
</script>

<style scoped>
.agent-chat__shell {
    display: flex;
    height: 100%;
    width: 100%;
    min-height: 0;
}

.agent-chat__panel {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    min-width: 0;
    flex: 1 1 auto;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
}

.agent-chat--full {
    display: flex;
    justify-content: center;
    height: 100%;
    min-height: 70vh;
}

.agent-chat--full .agent-chat__shell {
    max-width: 960px;
}

.agent-chat--side {
    width: 100%;
    max-width: 100%;
    height: 100%;
    min-height: 360px;
}

.agent-chat--floating .agent-chat__panel {
    border: none;
    border-radius: 0;
}
</style>
